import { LangLocale } from '@myre/common-i18n';
import { TagComponent } from '@myre/ui-primitive-components';
import {
  EmailDirective,
  LANGLOCALE,
  PhoneDirective,
  TranslatePipe,
} from '@myre/ui-primitive-core';
import { TooltipComponent } from '@myre/ui-primitive-tooltip';

import { DecimalPipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  inject,
  Input,
  Output,
  ViewChild,
  type OnChanges,
  type OnInit,
  type PipeTransform,
  type SimpleChanges,
} from '@angular/core';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  type ControlValueAccessor,
} from '@angular/forms';
import { isNil } from 'lodash-es';
import { NgxMaskDirective } from 'ngx-mask';

import {
  INPUT_TAG_CONFIG,
  type InputTagConfig,
} from '../../providers/input-tag-config';

export type TextInputType =
  | 'password'
  | 'number'
  | 'percent'
  | 'text'
  | 'email'
  | 'phone'
  | 'string'
  | 'search'
  | 'textarea';

export type ContentPosition = 'before' | 'after';

export interface Positionable {
  contentPosition?: ContentPosition;
}

const numberInputTypes: TextInputType[] = ['number', 'percent'];

export const TEXTBOX_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TextboxComponent),
  multi: true,
};

@Component({
  selector: 'myre-textbox',
  templateUrl: './textbox.component.html',
  styleUrls: ['./textbox.component.scss'],
  providers: [TEXTBOX_VALUE_ACCESSOR],
  imports: [
    TagComponent,
    FormsModule,
    NgxMaskDirective,
    PhoneDirective,
    EmailDirective,
    TooltipComponent,
    TranslatePipe,
  ],
})
export class TextboxComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  public readonly inputTagConfig = inject<InputTagConfig>(INPUT_TAG_CONFIG);
  private readonly _langLocale = inject<LangLocale>(LANGLOCALE);
  private readonly _decimalPipe = inject(DecimalPipe);

  @ViewChild(NgxMaskDirective, { static: true })
  public input: NgxMaskDirective;

  @ViewChild('input', { static: false })
  public inputElement: ElementRef;

  @HostBinding('class')
  public classes: string = 'myre-form__field';

  @HostBinding('class.error')
  @Input()
  public error: boolean = false;

  @HostBinding('class.warning')
  @Input()
  public warning: boolean = false;

  @HostBinding('class.secondary')
  @Input()
  public secondary: boolean = false;

  @HostBinding('class.display-only')
  @Input()
  public displayOnly: boolean = false;

  @Input()
  public displayOnlyNullValue: string;

  @Input()
  public picto: string;

  // Allow to display picto to the right of the input
  @Input()
  public isRightPicto: boolean = false;

  @Input()
  public type: TextInputType;

  @Input()
  public autocomplete: 'on' | 'off';

  @Input()
  public readonly: boolean;

  @HostBinding('attr.readonly')
  public get isReadonlyOrDisabled(): boolean {
    return this.readonly || this.disabled;
  }

  @Input()
  public min: number;

  @Input()
  public max: number;

  @Input()
  public id: string;

  @Input()
  public mask: string;

  @Input()
  public disabled: boolean = false;

  @Input()
  public required: boolean = true;

  @Input()
  public placeholder: string | null;

  @Input()
  public tooltip?: string;

  @Input()
  public pipe?: PipeTransform;

  @Input()
  public unitTag: string | null;

  @Input()
  public contentPosition: ContentPosition = 'before';

  @Output('onFocus')
  public onFocusEmitter = new EventEmitter<void>();

  @Output('onBlur')
  public onBlurEmitter = new EventEmitter<void>();

  @HostBinding('class.focus')
  public focused: boolean;

  @HostBinding('class.left-picto')
  public get leftPicto(): boolean {
    return this._isPictoVisible() && !this.isRightPicto;
  }

  @HostBinding('class.right-picto')
  public get rightPicto(): boolean {
    return this._isPictoVisible() && this.isRightPicto;
  }

  public formatted: string;
  public elementType: TextInputType;
  public value: string;

  @Output()
  public searchClick: EventEmitter<string> = new EventEmitter<string>();

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.pipe || changes.displayOnly || changes.displayOnlyNullValue) {
      this._formatValue(this._normalizeValue(this.value));
    }
  }

  public ngOnInit(): void {
    this.elementType = this.type;
  }

  public togglePasswordVisibility(): void {
    this.elementType = this.elementType === 'text' ? 'password' : 'text';
  }

  public onSearchClick(): void {
    this.searchClick.emit(this.value);
  }

  public writeValue(value: string | number): void {
    let strValue = '';

    if (
      typeof value === 'number' &&
      !isNaN(value) &&
      Math.abs(value) !== Infinity
    ) {
      strValue = String(value);
    }

    if (typeof value === 'string') {
      strValue = value;
    }

    this.value = String(strValue);
    this._formatValue(this._normalizeValue(value));

    void this.input.writeValue(this.value);
  }

  public registerOnChange(fn: (value: string | number) => any): void {
    this._onModelChange = fn;
  }

  public formattedClick(): void {
    this.inputElement.nativeElement.focus();
  }

  public registerOnTouched(fn: () => any): void {
    this._onTouched = fn;
  }

  public setDisabledState(val: boolean): void {
    this.disabled = val;
  }

  public onChange(value: string): void {
    let newVal: string | number = value;

    if (this.mask) {
      newVal = newVal.toString().replace(/[_ ]/g, '');
    }

    const normalized = this._normalizeValue(newVal);

    // Keep the raw text so the [ngModel] write-back doesn't strip in-progress decimals ("1.0" -> 1 erases ".0").
    this.value = isNil(newVal) ? '' : String(newVal);

    this._formatValue(normalized);
    this._onModelChange(normalized);
  }

  public onFocus(): void {
    this.onFocusEmitter.emit();
    this.focused = true;
  }

  public onBlur(): void {
    this.onBlurEmitter.emit();
    this.focused = false;
    this._onTouched();
  }

  public get isTypeNumber(): boolean {
    return numberInputTypes.includes(this.type);
  }

  public get isContentAfter(): boolean {
    return this.contentPosition === 'after';
  }

  private _normalizeValue(value: string | number): number | string {
    if (value === undefined) {
      return;
    }

    if ((this.isTypeNumber && String(value).trim() === '') || value === null) {
      return null;
    }

    return this.isTypeNumber ? +String(value).replace(',', '.') : value;
  }

  private _formatValue(val: number | string): void {
    if (isNil(val)) {
      this.formatted = (this.displayOnly && this.displayOnlyNullValue) || '';

      return;
    }

    if (this.isTypeNumber) {
      this.formatted = this._decimalPipe.transform(
        val,
        '1.0-6',
        this._langLocale,
      );

      if (this.pipe) {
        this.formatted = this.pipe.transform(this.formatted);
      }

      return;
    }

    this.formatted = '';
  }

  private _isPictoVisible(): boolean {
    return this.picto && this.type !== 'search';
  }

  private _onModelChange: (value: string | number) => any = () => {};

  private _onTouched: () => any = () => {};
}
